// Firebase Configuration for BurningTeam PWA
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyArxkIa_DvX5yYtP-JhsOixVkfdGHA2Y3o",
  authDomain: "burningteam-36157.firebaseapp.com",
  projectId: "burningteam-36157",
  storageBucket: "burningteam-36157.firebasestorage.app",
  messagingSenderId: "902640410338",
  appId: "1:902640410338:web:3a1f26e4b72b39da2688bb",
  measurementId: "G-F87H3JJNWZ"
};

const VAPID_KEY = "BN39gBicob34OIlTKlSHYue9dZ8eJhMvHU2UWzdF-TI6OV05uuvF-GgZM_k31miSiY5wiki9YrpzvhVQD29Q8JU";

// Initialize Firebase
let app;
let messaging;

try {
  app = initializeApp(firebaseConfig);
  // Note: Firebase Messaging will be initialized lazily when needed
  // This prevents automatic permission prompts on app load
  console.log('Firebase app initialized');
} catch (error) {
  console.error('Firebase initialization error:', error);
}

// Lazy initialization of Firebase Messaging - only when needed
async function ensureMessagingInitialized() {
  if (messaging) return messaging;
  
  if (typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator) {
    try {
      // Register the Firebase messaging service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      console.log('Firebase messaging SW registered:', registration.scope);
      window.firebaseSWRegistration = registration;
      messaging = getMessaging(app);
      return messaging;
    } catch (err) {
      console.log('Firebase messaging initialization failed:', err);
      return null;
    }
  }
  return null;
}

// Request notification permission and get FCM token
export async function requestNotificationPermission() {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return null;
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      
      // Initialize messaging NOW (lazy initialization)
      const msg = await ensureMessagingInitialized();
      
      if (msg) {
        try {
          // Wait for service worker registration
          let swRegistration = window.firebaseSWRegistration;
          if (!swRegistration) {
            // Try to get existing registration
            swRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
          }
          
          const tokenOptions = { vapidKey: VAPID_KEY };
          if (swRegistration) {
            tokenOptions.serviceWorkerRegistration = swRegistration;
            console.log('Using Firebase SW for token');
          }
          
          const token = await getToken(msg, tokenOptions);
          console.log('FCM Token:', token);
          // Save token for later use
          localStorage.setItem('fcmToken', token);
          return token;
        } catch (err) {
          console.log('Error getting FCM token:', err);
        }
      }
      return 'granted';
    } else {
      console.log('Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
}

// Refresh FCM token and update on server - call this on app start if notifications are enabled
export async function refreshFCMToken() {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return null;
    }
    
    // Ensure messaging is initialized
    const msg = await ensureMessagingInitialized();
    if (!msg) {
      console.log('Firebase messaging not initialized');
      return null;
    }
    
    // Get fresh token with service worker
    let swRegistration = window.firebaseSWRegistration;
    if (!swRegistration) {
      swRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    }
    
    const tokenOptions = { vapidKey: VAPID_KEY };
    if (swRegistration) {
      tokenOptions.serviceWorkerRegistration = swRegistration;
    }
    
    const token = await getToken(msg, tokenOptions);
    const oldToken = localStorage.getItem('fcmToken');
    
    // Only update if token changed
    if (token && token !== oldToken) {
      console.log('FCM Token refreshed (was different)');
      localStorage.setItem('fcmToken', token);
      return token;
    } else if (token) {
      console.log('FCM Token unchanged');
      return token;
    }
    
    return null;
  } catch (error) {
    console.error('Error refreshing FCM token:', error);
    return null;
  }
}

// Listen for foreground messages (when app is open)
export function onForegroundMessage(callback) {
  if (messaging) {
    return onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });
  }
  return () => {};
}

// Show a local notification (works when app is open)
// Uses ServiceWorkerRegistration.showNotification() for PWA compatibility
export async function showLocalNotification(title, options = {}) {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  try {
    // Try using Service Worker for PWA compatibility (required on mobile)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        icon: '/bt-192.png',
        badge: '/bt-32.png',
        vibrate: [200, 100, 200],
        tag: options.tag || 'burningteam-notification',
        renotify: true,
        body: options.body || '',
        data: options.data || {},
        ...options
      });
      console.log('Notification sent via Service Worker');
      return true;
    } else {
      // Fallback for browsers without active service worker
      const notification = new Notification(title, {
        icon: '/bt-192.png',
        badge: '/bt-32.png',
        vibrate: [200, 100, 200],
        tag: options.tag || 'burningteam-notification',
        renotify: true,
        ...options
      });

      // Auto close after 10 seconds
      setTimeout(() => notification.close(), 10000);

      // Handle click
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) options.onClick();
      };

      return notification;
    }
  } catch (error) {
    console.error('Error showing notification:', error);
    // Last resort fallback - try direct Notification
    try {
      const notification = new Notification(title, {
        icon: '/bt-192.png',
        body: options.body || '',
        ...options
      });
      setTimeout(() => notification.close(), 10000);
      return notification;
    } catch (e) {
      console.error('All notification methods failed:', e);
      return null;
    }
  }
}

// Schedule a notification for a specific time
export function scheduleNotification(title, options = {}, triggerTime) {
  const now = Date.now();
  const delay = triggerTime - now;

  if (delay <= 0) {
    console.log('Trigger time is in the past');
    return null;
  }

  const timeoutId = setTimeout(() => {
    showLocalNotification(title, options);
  }, delay);

  return timeoutId;
}

// Cancel a scheduled notification
export function cancelScheduledNotification(timeoutId) {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
}

export { app, messaging };
